import axios, { type AxiosInstance } from 'axios'
import { API_CONFIG } from '@/config/api'
import { setToken, getRefreshToken, clearAllTokens } from '@/utils/request/token'
import { message } from '@/utils/message/message'
import router from '@/router'

interface RefreshQueueItem {
  resolve: (token: string) => void
  reject: (error: unknown) => void
}

class TokenRefreshManager {
  private isRefreshing = false
  private refreshQueue: RefreshQueueItem[] = []

  /** 使用 refresh_token 换取新的 access_token */
  private async refreshToken(_axiosInstance: AxiosInstance): Promise<string> {
    const refreshToken = getRefreshToken()
    if (!refreshToken) {
      throw new Error('未找到 refresh token')
    }

    const response = await axios.post(
      `${API_CONFIG.baseURL}/auth/token/refresh/`,
      { refresh_token: refreshToken },
      {
        timeout: API_CONFIG.timeout as number,
        ...(API_CONFIG.withCredentials === undefined
          ? {}
          : { withCredentials: API_CONFIG.withCredentials }),
      }
    )

    const { access_token } = response.data.data
    setToken(access_token)
    return access_token
  }

  /** 处理并发请求时的 Token 刷新，确保只刷新一次 */
  async handleRefresh(axiosInstance: AxiosInstance): Promise<string> {
    if (this.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.refreshQueue.push({ resolve, reject })
      })
    }

    this.isRefreshing = true

    try {
      const newToken = await this.refreshToken(axiosInstance)
      this.refreshQueue.forEach(({ resolve }) => resolve(newToken))
      this.refreshQueue = []
      return newToken
    } catch (error) {
      this.refreshQueue.forEach(({ reject }) => reject(error))
      this.refreshQueue = []
      throw error
    } finally {
      this.isRefreshing = false
    }
  }

  /** 如有 refresh 正在进行则等待完成 */
  async waitForRefreshIfNeeded(): Promise<string | null> {
    if (!this.isRefreshing) return null
    return new Promise((resolve, reject) => {
      this.refreshQueue.push({ resolve, reject })
    })
  }

  /** 清除 Token 并跳转登录页，登录态过期提示通过全局消息展示，不写入 URL */
  redirectToLogin(tip = '登录已过期，请重新登录'): void {
    clearAllTokens()
    message.warning(tip)
    router.replace({ path: '/auth' })
  }
}

export const tokenRefreshManager = new TokenRefreshManager()
